import { useMemo, useState, type ReactNode } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Check, ChevronDown, Pencil, RotateCcw } from 'lucide-react'
import {
  ETICHETE_FAZA,
  ETICHETE_ROL_PARTENER,
  ETICHETE_STARE,
  ETICHETE_TIP,
  type FazaActivitate,
  type StareContainer,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Pastila } from '../../components/ui/Pastila'
import { DocumenteContainer } from '../../components/marketing/DocumenteContainer'
import { CosturiContainer } from '../../components/marketing/CosturiContainer'
import { FormularContainer } from '../../components/marketing/eveniment/FormularContainer'
import { FilaNecesar } from '../../components/marketing/eveniment/FilaNecesar'
import { FilaTransferuri } from '../../components/marketing/eveniment/FilaTransferuri'
import { FilaTeren } from '../../components/marketing/eveniment/FilaTeren'
import { FilaPredari } from '../../components/marketing/eveniment/FilaPredari'
import { FilaRaport } from '../../components/marketing/eveniment/FilaRaport'
import { FilaGrafice } from '../../components/marketing/eveniment/FilaGrafice'
import { FilaDeFacut } from '../../components/marketing/eveniment/FilaDeFacut'
import { FilaIstoric } from '../../components/marketing/eveniment/FilaIstoric'
import { InchideActivitatea } from '../../components/marketing/eveniment/InchideActivitatea'
import { RezumatActivitate } from '../../components/marketing/eveniment/RezumatActivitate'
import { CeUrmeaza, type FilaNoua, type Pas } from '../../components/marketing/eveniment/CeUrmeaza'
import {
  PatratTip,
  TON_FAZA,
  candRelativ,
  cuFaza,
  semnale,
} from '../../components/marketing/eveniment/activitate'
import { EchipaEveniment } from '../../components/marketing/eveniment/EchipaEveniment'
import {
  despre,
  eroare,
  perioada,
  type Campanie,
  type UtilizatorScurt,
} from '../../components/marketing/eveniment/comun'

/**
 * Pagina unei activitati din PR & Evenimente (redesign 15.10.2026, macheta aprobata de Madalin).
 *
 * Unsprezece file au devenit sase: ce arata aceleasi lucruri s-a comasat, nimic nu s-a pierdut.
 * Rezumatul activitatii sta mereu in dreapta, pe orice fila. Prima fila raspunde la „ce urmeaza",
 * din date — nu din bife puse de mana.
 */

type VedereMarfa = 'cereri' | 'transferuri' | 'predari' | 'raport'

const FILE: { cheie: FilaNoua; eticheta: string }[] = [
  { cheie: 'prezentare', eticheta: 'Prezentare' },
  { cheie: 'marfa', eticheta: 'Marfă' },
  { cheie: 'bani', eticheta: 'Bani' },
  { cheie: 'teren', eticheta: 'Pe teren' },
  { cheie: 'fisiere', eticheta: 'Fișiere' },
  { cheie: 'activitate', eticheta: 'Activitate' },
]

const VEDERI_MARFA: { cheie: VedereMarfa; eticheta: string }[] = [
  { cheie: 'cereri', eticheta: 'Cereri' },
  { cheie: 'transferuri', eticheta: 'Transferuri' },
  { cheie: 'predari', eticheta: 'Predări & PV' },
  { cheie: 'raport', eticheta: 'Raport pe produs' },
]

/*
  Adresele vechi raman bune: emailul si notificarea unei cereri duc la `?fila=necesar&cerere=…`,
  iar lista si alte ecrane la `?fila=documente`, `?fila=grafica`. Fiecare fila veche are locul ei
  in cele noi.
*/
const DIN_VECHI: Record<string, [FilaNoua, VedereMarfa?]> = {
  sumar: ['prezentare'],
  necesar: ['marfa', 'cereri'],
  transferuri: ['marfa', 'transferuri'],
  predari: ['marfa', 'predari'],
  raport: ['marfa', 'raport'],
  costuri: ['bani'],
  documente: ['fisiere'],
  grafica: ['fisiere'],
  defacut: ['activitate'],
  istoric: ['activitate'],
}

function dinAdresa(fila: string | null, vedere: string | null): [FilaNoua, VedereMarfa] {
  const v = VEDERI_MARFA.some((x) => x.cheie === vedere) ? (vedere as VedereMarfa) : 'cereri'
  if (fila && FILE.some((f) => f.cheie === fila)) return [fila as FilaNoua, v]
  const vechi = fila ? DIN_VECHI[fila] : undefined
  if (vechi) return [vechi[0], vechi[1] ?? v]
  return ['prezentare', v]
}

interface Sumar {
  faza: FazaActivitate
  pasi: Pas[]
  plata: Campanie['plata']
  plataMotiv: string | null
  areMostre: boolean
  cereriDeAprobat: number
  actiuniTeren: number
  angajat: number
  cheltuit: number
  documentsCount: number
  actionsCount: number
  overdueActionsCount: number
  cerereUitata?: boolean
  raport: { text: string; la: string; de: string | null } | null
}

const FAZE_PASI: FazaActivitate[] = [
  'propunere',
  'pregatire',
  'desfasurare',
  'de_inchis',
  'incheiata',
]

export function EvenimentPage() {
  const { id } = useParams<{ id: string }>()
  const campanieId = Number(id)
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.events:write')
  const poateAproba = permissions.includes('marketing.necesar:aproba')

  const [parametri, setParametri] = useSearchParams()
  const [fila, vedere] = dinAdresa(parametri.get('fila'), parametri.get('vedere'))
  const [editare, setEditare] = useState(false)
  /** Fereastra de inchidere: `inchide` cu raport, `anuleaza` cu motiv. */
  const [inchidere, setInchidere] = useState<'inchide' | 'anuleaza' | null>(null)
  const [rezumatDeschis, setRezumatDeschis] = useState(false)

  /*
    Fila si vederea stau in adresa: un refresh sau un link trimis cuiva deschide exact acelasi
    loc. `cerere` ramane doar cat esti pe Marfa — altfel, la intoarcere, fereastra de aprobare s-ar
    redeschide singura.
  */
  function mergiLa(f: FilaNoua, v?: VedereMarfa, cerere?: number) {
    const p = new URLSearchParams()
    p.set('fila', f)
    if (f === 'marfa') {
      p.set('vedere', v ?? vedere)
      if (cerere) p.set('cerere', String(cerere))
    }
    setParametri(p, { replace: true })
  }

  const q = useQuery<{ campanie: Campanie }>({
    queryKey: ['campanie', campanieId],
    queryFn: async () => (await api.get(`/campanii/${campanieId}`)).data,
    enabled: Number.isFinite(campanieId),
  })
  const sumar = useQuery<{ summary: Sumar }>({
    queryKey: ['campanie', campanieId, 'summary'],
    queryFn: async () => (await api.get(`/campanii/${campanieId}/summary`)).data,
    enabled: Number.isFinite(campanieId) && q.isSuccess,
  })
  const utilizatori = useQuery<{ users: UtilizatorScurt[] }>({
    queryKey: ['settings-users-scurt'],
    queryFn: async () =>
      (await api.get<{ users: UtilizatorScurt[] }>('/settings/users/echipa')).data,
  })

  const actualizeaza = useMutation({
    mutationFn: async (date: Record<string, unknown>) =>
      (await api.patch(`/campanii/${campanieId}`, date)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campanie', campanieId] })
      void queryClient.invalidateQueries({ queryKey: ['campanii'] })
      toast.success('Salvat')
      setEditare(false)
    },
    onError: eroare,
  })

  const c = q.data?.campanie
  const s = sumar.data?.summary
  /*
    Rezumatul din dreapta e aceeasi piesa ca in panoul din lista. Fara rezumatul de pe server
    (inca se incarca, sau a cazut) faza se calculeaza din activitate, iar cifrele lipsesc.
  */
  const activitate = useMemo(() => {
    if (!c) return null
    const baza = cuFaza(c)
    return {
      ...baza,
      faza: s?.faza ?? baza.faza,
      costTotal: s?.cheltuit,
      angajat: s?.angajat,
      plata: s?.plata,
      plataMotiv: s?.plataMotiv,
      areMostre: s?.areMostre,
      cereriDeAprobat: s?.cereriDeAprobat,
      cerereUitata: s?.cerereUitata,
      _count: { actiuniSampling: s?.actiuniTeren ?? 0 },
    }
  }, [c, s])

  // Un id care nu e numar nu porneste nicio cerere: fara asta pagina ar sta la „Se incarca".
  if (q.isError || !Number.isInteger(campanieId) || campanieId <= 0) {
    return (
      <div className="grid gap-2 p-6">
        <p className="text-corp text-b4l-text-muted">Activitatea nu există sau n-ai acces la ea.</p>
        <Link to="/marketing/evenimente" className="text-corp text-b4l-blue-dark hover:underline">
          Înapoi la PR & Evenimente
        </Link>
      </div>
    )
  }
  if (!c || !activitate) return <div className="p-6 text-corp text-b4l-text-muted">Se încarcă…</div>

  const responsabil = c.responsabilId
    ? (utilizatori.data?.users.find((u) => u.id === c.responsabilId)?.name ??
      c.responsabil?.email ??
      null)
    : null
  const nrSemnale = semnale(activitate).length
  const insigne: Partial<Record<FilaNoua, [string, string]>> = {}
  if (s?.cereriDeAprobat)
    insigne.marfa = [
      'bg-b4l-red-light text-b4l-red-dark',
      s.cereriDeAprobat === 1 ? '1 de aprobat' : `${s.cereriDeAprobat} de aprobat`,
    ]
  if (s?.overdueActionsCount)
    insigne.activitate = [
      'bg-b4l-red-light text-b4l-red-dark',
      `${s.overdueActionsCount} întârziate`,
    ]
  else if (s?.actionsCount)
    insigne.activitate = ['bg-b4l-bg text-b4l-text-dim', `${s.actionsCount}`]
  if (s?.documentsCount) insigne.fisiere = ['bg-b4l-bg text-b4l-text-dim', `${s.documentsCount}`]

  return (
    <div className="grid gap-4">
      <header className="grid gap-3">
        <Link
          to="/marketing/evenimente"
          className="inline-flex items-center gap-1.5 justify-self-start text-mic text-b4l-blue-dark hover:underline"
        >
          <ArrowLeft size={14} /> PR & Evenimente
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="grid min-w-0 gap-1">
            <h1 className="flex items-start gap-2.5 break-words text-titlu text-b4l-text">
              <PatratTip tip={c.tip} eticheta={ETICHETE_TIP[c.tip]} />
              <span className="min-w-0 pt-0.5">{c.titlu}</span>
            </h1>
            <p className="text-mic text-b4l-text-muted">
              {ETICHETE_TIP[c.tip]} · {perioada(c)}
              {(c.locatie || c.oras) &&
                ` · ${[c.locatie, c.oras].filter(Boolean).join(', ')}`} ·{' '}
              <span className="font-mono text-[11.5px]">{c.cod}</span>
              {c.generatAutomat && ' · creată automat'}
            </p>
          </div>
          {poateScrie && !c.generatAutomat && (
            <div className="flex flex-wrap items-center gap-2">
              {/* Anularea, rară, ca acțiune discretă; închiderea, butonul principal când e timpul. */}
              {!['incheiata', 'anulata'].includes(activitate.faza) && (
                <button
                  type="button"
                  onClick={() => setInchidere('anuleaza')}
                  className="rounded-caseta px-2.5 py-1.5 text-corp text-b4l-text-muted hover:bg-b4l-red-light hover:text-b4l-red-dark"
                >
                  Anulează
                </button>
              )}
              <button
                type="button"
                onClick={() => setEditare(true)}
                className="inline-flex items-center gap-1.5 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp hover:border-b4l-blue"
              >
                <Pencil size={14} /> Editează
              </button>
              {(activitate.faza === 'desfasurare' || activitate.faza === 'de_inchis') && (
                <button
                  type="button"
                  onClick={() => setInchidere('inchide')}
                  className="rounded-caseta bg-b4l-blue px-3 py-1.5 text-corp font-semibold text-white hover:opacity-90"
                >
                  Închide cu raport
                </button>
              )}
            </div>
          )}
        </div>
        <Faze faza={activitate.faza} />
        <nav
          role="tablist"
          aria-label="File"
          className="-mx-4 flex gap-0.5 overflow-x-auto border-b border-b4l-border px-4 md:mx-0 md:px-0"
        >
          {FILE.map((f) => {
            const ales = fila === f.cheie
            const ins = insigne[f.cheie]
            return (
              <button
                key={f.cheie}
                type="button"
                role="tab"
                aria-selected={ales}
                onClick={() => mergiLa(f.cheie)}
                className={`-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-corp ${
                  ales
                    ? 'border-b4l-blue font-semibold text-b4l-text'
                    : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
                }`}
              >
                {f.eticheta}
                {ins && (
                  <span className={`rounded-full px-1.5 text-[11px] font-semibold ${ins[0]}`}>
                    {ins[1]}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </header>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <aside
          aria-label="Rezumat"
          className="grid gap-4 rounded-xl border border-b4l-border bg-b4l-surface p-4 lg:sticky lg:top-4 lg:order-last"
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <Pastila ton={TON_FAZA[activitate.faza]}>{ETICHETE_FAZA[activitate.faza]}</Pastila>
            <span className="text-mic text-b4l-text-dim">{candRelativ(c)}</span>
            {/*
              Pe telefon rezumatul sta deasupra si, deschis, ocupa tot primul ecran — „Ce urmeaza"
              ajungea mult mai jos. Acolo e restrans; pe calculator e mereu deschis.
            */}
            <button
              type="button"
              onClick={() => setRezumatDeschis(!rezumatDeschis)}
              aria-expanded={rezumatDeschis}
              className="ml-auto inline-flex items-center gap-1 rounded-caseta px-2 py-1 text-mic text-b4l-blue-dark hover:bg-b4l-bg lg:hidden"
            >
              {rezumatDeschis
                ? 'Ascunde'
                : nrSemnale
                  ? `Rezumat · ${nrSemnale} ${nrSemnale === 1 ? 'cere' : 'cer'} atenție`
                  : 'Rezumat'}
              <ChevronDown size={14} className={rezumatDeschis ? 'rotate-180' : ''} />
            </button>
          </div>
          <div className={`${rezumatDeschis ? 'grid' : 'hidden'} gap-4 lg:grid`}>
            <RezumatActivitate a={activitate} numeResponsabil={responsabil} />
          </div>
          {sumar.isError && (
            <p className="text-mic text-b4l-red-dark">
              Cifrele n-au putut fi încărcate.{' '}
              <button type="button" onClick={() => sumar.refetch()} className="underline">
                Reîncearcă
              </button>
            </p>
          )}
        </aside>

        <div className="grid min-w-0 gap-4">
          {fila === 'prezentare' && (
            <>
              <Card title="Ce urmează">
                {sumar.isLoading ? (
                  <p className="text-mic text-b4l-text-muted">Se încarcă…</p>
                ) : sumar.isError ? (
                  <p className="flex items-center gap-2 text-mic text-b4l-red-dark">
                    Pașii n-au putut fi încărcați.
                    <button
                      type="button"
                      onClick={() => sumar.refetch()}
                      className="inline-flex items-center gap-1 underline"
                    >
                      <RotateCcw size={12} /> Reîncearcă
                    </button>
                  </p>
                ) : (
                  <CeUrmeaza
                    pasi={s?.pasi ?? []}
                    poateAproba={poateAproba}
                    onInchideActivitatea={
                      poateScrie && !c.generatAutomat ? () => setInchidere('inchide') : undefined
                    }
                    onDeschide={(f, cerere) =>
                      mergiLa(f, f === 'marfa' ? 'cereri' : undefined, cerere)
                    }
                  />
                )}
              </Card>
              {s?.raport && (
                <Card title={activitate.faza === 'anulata' ? 'De ce s-a anulat' : 'Raportul'}>
                  <p className="whitespace-pre-line text-corp text-b4l-text">{s.raport.text}</p>
                  <p className="mt-2 text-mic text-b4l-text-dim">
                    {s.raport.de ? `${s.raport.de} · ` : ''}
                    {new Date(s.raport.la).toLocaleDateString('ro-RO')}
                  </p>
                </Card>
              )}
              <EchipaEveniment
                campanieId={c.id}
                titlu={c.titlu}
                dataStart={c.dataStart}
                dataFinal={c.dataFinal}
              />
              <Detalii c={c} responsabil={responsabil} />
            </>
          )}

          {fila === 'marfa' && (
            <>
              <div role="group" aria-label="Vederi marfă" className="flex flex-wrap gap-1.5">
                {VEDERI_MARFA.map((v) => (
                  <button
                    key={v.cheie}
                    type="button"
                    aria-pressed={vedere === v.cheie}
                    onClick={() => mergiLa('marfa', v.cheie)}
                    className={`rounded-full border px-3 py-1 text-[12.5px] ${
                      vedere === v.cheie
                        ? 'border-b4l-text bg-b4l-text text-b4l-surface'
                        : 'border-b4l-border bg-b4l-surface hover:bg-b4l-bg-soft'
                    }`}
                  >
                    {v.eticheta}
                  </button>
                ))}
              </div>
              {vedere === 'cereri' && <FilaNecesar campanieId={c.id} cod={c.cod} />}
              {vedere === 'transferuri' && <FilaTransferuri campanieId={c.id} />}
              {vedere === 'predari' && <FilaPredari campanieId={c.id} />}
              {vedere === 'raport' && <FilaRaport campanieId={c.id} />}
            </>
          )}

          {fila === 'bani' && <CosturiContainer campanieId={c.id} canWrite={poateScrie} />}
          {fila === 'teren' && <FilaTeren campanieId={c.id} />}

          {fila === 'fisiere' && (
            <>
              <Card title="Documente și poze">
                <DocumenteContainer
                  tip="campanie"
                  parinteId={c.id}
                  canWrite={poateScrie}
                  contractSponsorizare={c.tip === 'sponsorizare'}
                />
              </Card>
              <FilaGrafice campanieId={c.id} canWrite={poateScrie} />
            </>
          )}

          {fila === 'activitate' && (
            <>
              <FilaDeFacut campanieId={c.id} />
              <FilaIstoric campanieId={c.id} />
            </>
          )}
        </div>
      </div>

      {inchidere && (
        <InchideActivitatea
          campanieId={c.id}
          tip={c.tip}
          titlu={c.titlu}
          anulare={inchidere === 'anuleaza'}
          avertisment={
            inchidere === 'inchide' && s?.cerereUitata
              ? 'O cerere de marfă e aprobată, dar marfa n-a plecat toată. Poți închide, dar verific-o pe fila Marfă.'
              : null
          }
          onInchide={() => setInchidere(null)}
        />
      )}
      {editare && (
        <FormularContainer
          titlu={`Editează ${c.cod}`}
          etichetaSalvare="Salvează"
          initial={c}
          utilizatori={utilizatori.data?.users ?? []}
          onClose={() => setEditare(false)}
          onSubmit={(date) => actualizeaza.mutate(date)}
          seSalveaza={actualizeaza.isPending}
        />
      )}
    </div>
  )
}

/** Pasii fazelor, cu cea de acum evidentiata. Anulata nu e o faza pe drum — se spune ca atare. */
function Faze({ faza }: { faza: FazaActivitate }) {
  if (faza === 'anulata')
    return (
      <p className="text-mic text-b4l-text-muted">
        <Pastila ton="gri">Anulată</Pastila>
      </p>
    )
  const acum = FAZE_PASI.indexOf(faza)
  return (
    <ol aria-label="Faza" className="flex flex-wrap items-center gap-1">
      {FAZE_PASI.map((f, i) => (
        <li key={f} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden className="h-px w-3 bg-b4l-border" />}
          <span
            aria-current={i === acum ? 'step' : undefined}
            className={`inline-flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2.5 text-mic ${
              i === acum
                ? 'bg-b4l-blue-light font-semibold text-b4l-blue-dark'
                : i < acum
                  ? 'text-b4l-text-muted'
                  : 'text-b4l-text-dim'
            }`}
          >
            <span
              aria-hidden
              className={`grid h-[18px] w-[18px] place-items-center rounded-full text-[10px] font-bold ${
                i < acum
                  ? 'bg-b4l-green text-white'
                  : i === acum
                    ? 'border-[1.5px] border-b4l-blue'
                    : 'border-[1.5px] border-b4l-border'
              }`}
            >
              {i < acum ? <Check size={11} /> : i + 1}
            </span>
            {ETICHETE_FAZA[f]}
          </span>
        </li>
      ))}
    </ol>
  )
}

/** Toate campurile activitatii — ce era pe fila Sumar. */
function Detalii({ c, responsabil }: { c: Campanie; responsabil: string | null }) {
  const randuri: [string, ReactNode][] = [
    ['Cod', c.cod],
    ['Tip', ETICHETE_TIP[c.tip]],
    ['Stare', ETICHETE_STARE[c.status as StareContainer] ?? c.status],
    ['Perioadă', perioada(c)],
    ['Responsabil', responsabil],
    ['Temă', c.tema],
    ['Oraș', c.oras],
    ['Locație', c.locatie],
    [
      'Parteneri',
      (c.parteneri ?? []).length
        ? (c.parteneri ?? [])
            .map(
              (p) =>
                `${despre(p).nume} (${ETICHETE_ROL_PARTENER[p.rol]}${p.nota ? `, ${p.nota}` : ''})`,
            )
            .join(' · ')
        : null,
    ],
    [
      'Organizator scris înainte',
      c.parteneri?.some((p) => p.rol === 'organizator') ? null : c.organizator,
    ],
    [
      'Persoană de contact',
      [c.contactNume, c.contactEmail, c.contactTelefon].filter(Boolean).join(' · ') || null,
    ],
    ['Participanți estimați', c.participantiEstimati?.toString() ?? null],
    ['Ce am agreat să le dăm', c.ceAmAgreat],
    [
      'Taxă / pachet',
      c.taxaParticipare
        ? `${Number(c.taxaParticipare).toLocaleString('ro-RO')} ${c.taxaMoneda ?? 'RON'}`
        : null,
    ],
    ['Canale', c.canaleSociale],
    ['Observații', c.observatii],
  ]
  return (
    <Card title="Detalii">
      <dl className="grid gap-x-6 sm:grid-cols-2">
        {randuri
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => (
            <div key={k} className="flex gap-3 border-b border-b4l-border/60 py-1.5 text-corp">
              <dt className="w-40 shrink-0 text-b4l-text-muted">{k}</dt>
              <dd className="min-w-0 break-words text-b4l-text">{v}</dd>
            </div>
          ))}
      </dl>
    </Card>
  )
}
